// mvp-returns Phase H9 — Propose Template Manager page

import { Button, Group, Modal, Stack, Table, Text } from "@mantine/core";
import { ConfirmNote, DisabledReason, EmptyStateGuide, HelpButtonGroup, i18n, nextStepToast } from "@dehub/theme";
import { ArchiveIcon, ListBulletsIcon, PencilSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ProposeTemplateApi } from "../../../apis";
import type { IProposeTemplate } from "../../../apis/propose-template";
import { DeliveryRowAction } from "../../../components/delivery/action-controls";
import { TableFrame } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { usePermission } from "../../../components/permission";
import { TemplateFormDrawer } from "../../../components/return-settings/template-form-drawer";
import { DeniedCard, HelpColumnTitle, ListState } from "../../../components/ui";
import { message } from "../../../utils/message";

/** @dehub/help key namespace of this screen (spec 12 §0). */
const HELP = "order.returns-settings-propose-templates";

// Lining figures so percentages align down a column.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

const breadcrumb = () => [
  { to: "/",                  label: i18n.t("returns:breadcrumb.home") },
  { to: "/returns",           label: i18n.t("returns:breadcrumb.returns") },
  { to: "/returns/settings",  label: i18n.t("returns:breadcrumb.settings") },
  { label: i18n.t("returns:settings.proposeTemplates.title") },
];

export function ProposeTemplatesPage() {
  const { t } = useTranslation("returns");
  // BE propose-template: list = RETURN_VIEW, create/update/delete = RETURN_PROPOSE.
  const view = usePermission("RETURN_VIEW");
  const write = usePermission("RETURN_PROPOSE");
  const writeState = { allowed: write.hasAccess, reason: write.reason };

  const [drawerOpened, setDrawerOpened] = useState(false);
  const [editTarget, setEditTarget]     = useState<IProposeTemplate | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<IProposeTemplate | null>(null);
  const [archiving, setArchiving]       = useState(false);

  const { data, error, isLoading, mutate } = useSWR(
    view.hasAccess ? ["propose-templates"] : null,
    () => ProposeTemplateApi.list(),
    { revalidateOnFocus: false }
  );
  const rows = data?.items ?? [];

  const handleNew = () => { setEditTarget(null); setDrawerOpened(true); };
  const handleEdit = (template: IProposeTemplate) => { setEditTarget(template); setDrawerOpened(true); };

  // O180 — the BE only flags the row archived (it stays in the table), so the dialog says «архивлах», not «устгах».
  const confirmArchive = async () => {
    if (!archiveTarget) return;
    try {
      setArchiving(true);
      await ProposeTemplateApi.remove(archiveTarget.id);
      nextStepToast(`${HELP}.toast.archived`, { fallback: { title: t("proposeTemplatesPage.archived") } });
      setArchiveTarget(null);
      mutate();
    } catch (err) {
      message.error(err instanceof Error ? err.message : t("notifTemplatesPage.deleteFailed"));
    } finally {
      setArchiving(false);
    }
  };

  if (!view.hasAccess) {
    return (
      <PageLayout title={t("settings.proposeTemplates.title")} breadcrumb={breadcrumb()}>
        <DeniedCard permission="RETURN_VIEW" />
      </PageLayout>
    );
  }

  // Kept above the empty state too, so an empty list still shows its columns (Ord_ReturnTemplates).
  const thead = (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{t("preventionTable.actions")}</Table.Th>
        <Table.Th>{t("templateForm.name")}</Table.Th>
        <Table.Th>{t("filters.resolutionLabel")}</Table.Th>
        <Table.Th ta="right"><HelpColumnTitle helpKey={`${HELP}.field.col-refund-percent`}>{t("proposeTemplatesPage.col.refundPercent")}</HelpColumnTitle></Table.Th>
        <Table.Th>{t("templateForm.refundMethodLabel")}</Table.Th>
      </Table.Tr>
    </Table.Thead>
  )

  return (
    <PageLayout
      title={t("settings.proposeTemplates.title")}
      subTitle={t("proposeTemplatesPage.subtitle")}
      breadcrumb={breadcrumb()}
      extra={[
        // @dehub/help: attached «?» (R2) + reason popover without the permission (R4).
        <DisabledReason key="new" helpKey={`${HELP}.reason.write`} reason="PERM" when={!write.hasAccess} position="bottom-end">
          <HelpButtonGroup helpKey={`${HELP}.action.new`}>
            <Button data-help-id="returns-propose-template-new" leftSection={<PlusIcon size={16} />} onClick={handleNew}>
              {t("notifTemplatesPage.newTemplate")}
            </Button>
          </HelpButtonGroup>
        </DisabledReason>,
      ]}
    >
      <Stack gap="md">
        <TableFrame>
          {error && !data ? (
            <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          ) : isLoading && !data ? (
            <ListState state="loading" columns={5} />
          ) : rows.length === 0 ? (
            <>
              <Table verticalSpacing="sm" horizontalSpacing="md">
                {thead}
              </Table>
            {/* @dehub/help: why the list is empty and what to do (R9); the plain state stays the fallback. */}
            <EmptyStateGuide
              helpKey={`${HELP}.empty.main`}
              icon={<ListBulletsIcon size={24} />}
              actions={{ create: () => { if (write.hasAccess) handleNew(); } }}
              fallback={
                <ListState
                  state="empty"
                  title={t("proposeTemplatesPage.empty")}
                  description={t("proposeTemplatesPage.emptyHint")}
                  icon={<ListBulletsIcon size={24} />}
                />
              }
            />
            </>
          ) : (
            <Table.ScrollContainer minWidth={800}>
              <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                {thead}
                <Table.Tbody>
                  {rows.map((row) => (
                    <Table.Tr key={row.id}>
                      <Table.Td>
                        <Group gap="xs" wrap="nowrap">
                          <DeliveryRowAction label={t("notifTemplatesPage.edit")} icon={<PencilSimpleIcon />} state={writeState} onClick={() => handleEdit(row)} />
                          <DeliveryRowAction label={t("proposeTemplatesPage.archive")} icon={<ArchiveIcon />} state={writeState} onClick={() => setArchiveTarget(row)} />
                        </Group>
                      </Table.Td>
                      <Table.Td maw={320}>
                        <Text size="sm" fw={500}>{row.name}</Text>
                        {row.description ? <Text size="sm" c="var(--nw-text-2)" lineClamp={1}>{row.description}</Text> : null}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{t(`proposeTemplatesPage.resolution.${row.resolutionType}`, { defaultValue: "-" })}</Text>
                      </Table.Td>
                      <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{row.refundPercent}%</Text></Table.Td>
                      <Table.Td>
                        <Text size="sm">{row.refundMethod ? t(`settings.refundMethod.${row.refundMethod}`, { defaultValue: "-" }) : "-"}</Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </TableFrame>
      </Stack>

      <TemplateFormDrawer
        opened={drawerOpened}
        template={editTarget}
        onClose={() => setDrawerOpened(false)}
        onSaved={() => mutate()}
      />

      {/* Archive confirmation (440) */}
      <Modal opened={!!archiveTarget} onClose={() => setArchiveTarget(null)} title={t("proposeTemplatesPage.deleteTitle")} size={440}>
        <Stack gap="md">
          <Text size="sm">{t("proposeTemplatesPage.deleteConfirm")}</Text>
          {archiveTarget ? <Text size="sm" fw={500}>{archiveTarget.name}</Text> : null}
          <ConfirmNote helpKey={`${HELP}.confirm.archive`} mt={0} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setArchiveTarget(null)}>{t("templateForm.close")}</Button>
            <Button onClick={confirmArchive} loading={archiving}>{t("proposeTemplatesPage.archive")}</Button>
          </Group>
        </Stack>
      </Modal>
    </PageLayout>
  );
}
